import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '../../context/ThemeContext';
import { formatMoney } from '../../lib/formatMoney';
import { withAlpha } from '../../lib/chartColors';
import {
  buildSemanticMultiSeries,
  granularityLabel,
} from '../../lib/timeSeriesAggregation';

const CHART_FONT = 'Montserrat, ui-sans-serif, system-ui, sans-serif';

const SOURCE_META = [
  { key: 'cmp', label: 'CMP', colorIndex: 0 },
  { key: 'pos', label: 'POS', colorIndex: 2 },
  { key: 'app', label: 'APP', colorIndex: 3 },
];

const VALUE_KEYS = SOURCE_META.map((s) => s.key);

/**
 * Source-split revenue over time with the same semantic zoom as other dashboards:
 * long ranges roll up to week/month; zoom in for daily detail.
 * @param {'stacked' | 'lines'} chartType
 */
export function RevenueMovementChart({
  series = [],
  currencyCode = 'DKK',
  chartType = 'stacked',
  height = 320,
  className = '',
}) {
  const { chartPalette, chartLine } = useTheme();
  const isLines = chartType === 'lines';

  const sortedSeries = useMemo(
    () => [...series].sort((a, b) => String(a.date).localeCompare(String(b.date))),
    [series]
  );

  const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });

  useEffect(() => {
    setZoomRange({ start: 0, end: 100 });
  }, [sortedSeries.length]);

  const view = useMemo(
    () =>
      buildSemanticMultiSeries(sortedSeries, zoomRange.start, zoomRange.end, VALUE_KEYS),
    [sortedSeries, zoomRange.start, zoomRange.end]
  );

  const option = useMemo(() => {
    const categories = view.points.map((row) => row.label);
    const dense = view.points.length > 40;
    const veryDense = view.points.length > 80;
    const colors = SOURCE_META.map((s) => chartPalette[s.colorIndex] || chartPalette[0]);

    return {
      color: colors,
      animationDuration: 280,
      grid: { left: 8, right: 12, top: 68, bottom: 52, containLabel: true },
      legend: {
        top: 0,
        left: 0,
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
        padding: [0, 0, 20, 0],
        textStyle: { fontFamily: CHART_FONT, fontSize: 11, color: '#475569' },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: isLines ? 'cross' : 'shadow' },
        backgroundColor: '#fff',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: { fontFamily: CHART_FONT, color: '#0f172a', fontSize: 12 },
        formatter(params) {
          const index = params?.[0]?.dataIndex;
          const point = view.points[index];
          const heading = point?.date
            ? view.granularity === 'month'
              ? point.date.slice(0, 7)
              : point.date
            : params?.[0]?.axisValueLabel || '';
          const rows = (params || [])
            .map(
              (p) =>
                `${p.marker} ${p.seriesName}: <b>${formatMoney(p.value, { currency: currencyCode })}</b>`
            )
            .join('<br/>');
          const total = (params || []).reduce((sum, p) => sum + Number(p.value || 0), 0);
          return `${heading}<br/>${rows}<br/>Total: <b>${formatMoney(total, { currency: currencyCode })}</b>`;
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        boundaryGap: !isLines,
        axisLabel: {
          fontFamily: CHART_FONT,
          fontSize: 10,
          color: '#64748b',
          hideOverlap: true,
          interval: dense ? Math.ceil(categories.length / 6) : 'auto',
          rotate: dense ? 35 : 0,
        },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#e2e8f0' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: CHART_FONT,
          fontSize: 10,
          color: '#64748b',
          formatter: (v) => formatMoney(v, { currency: currencyCode, compact: true }),
        },
        splitLine: { lineStyle: { color: '#f1f5f9' } },
      },
      dataZoom: [
        {
          type: 'inside',
          start: zoomRange.start,
          end: zoomRange.end,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
        },
        {
          type: 'slider',
          start: zoomRange.start,
          end: zoomRange.end,
          height: 18,
          bottom: 6,
          borderColor: '#e2e8f0',
          fillerColor: chartLine?.sliderFill || 'rgba(79, 70, 229, 0.15)',
          handleStyle: {
            color: chartLine?.handle || '#4f46e5',
            borderColor: chartLine?.handle || '#4f46e5',
          },
          textStyle: { color: '#64748b', fontSize: 10, fontFamily: CHART_FONT },
        },
      ],
      series: SOURCE_META.map((source, index) => {
        const data = view.points.map((row) => Number(row[source.key] || 0));
        if (isLines) {
          return {
            name: source.label,
            type: 'line',
            smooth: 0.35,
            showSymbol: !dense,
            symbolSize: 6,
            data,
            lineStyle: { width: 2.5 },
            areaStyle: {
              color: withAlpha(colors[index], 0.08),
            },
            emphasis: { focus: 'series' },
            sampling: data.length > 200 ? 'lttb' : undefined,
          };
        }
        return {
          name: source.label,
          type: 'bar',
          stack: 'revenue',
          barMaxWidth: veryDense ? 8 : dense ? 14 : 28,
          barCategoryGap: veryDense ? '28%' : dense ? '35%' : '42%',
          data,
          emphasis: { focus: 'series' },
        };
      }),
    };
  }, [view, currencyCode, chartPalette, chartLine, isLines, zoomRange.start, zoomRange.end]);

  const handleDataZoom = useCallback((params) => {
    const payload = params.batch?.[0] || params;
    setZoomRange({
      start: payload.start ?? 0,
      end: payload.end ?? 100,
    });
  }, []);

  if (!sortedSeries.length) return null;

  return (
    <div className={`flex w-full min-w-0 flex-col gap-1 ${className}`.trim()}>
      <ReactECharts
        key={chartType}
        option={option}
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
        lazyUpdate
        onEvents={{ datazoom: handleDataZoom }}
        aria-label="Revenue movement by source over time"
      />
      <p className="text-center text-[10px] text-slate-400">
        {granularityLabel(view.granularity)} view · scroll or drag to zoom
      </p>
    </div>
  );
}
